import React from 'react';
import './LocationSection.css';
import LocationMap from './LocationMap';

const LocationSection = ({ formData, updateAddress, errors = {}, canEdit = true }) => {
    const address = formData.address || {};

    const coordinates = address.geo?.coordinates || [0, 0];

    const updateCoordinate = (index, value) => {
        const newCoordinates = [...coordinates];

        newCoordinates[index] = value === '' ? '' : Number(value);

        updateAddress('geo', {
            type: 'Point',
            coordinates: newCoordinates
        });
    };

    return (
        <div className='location-section'>
            {/* =====================================================
                ADDRESS
            ===================================================== */}

            <section className='form-section'>
                <div className='section-heading'>
                    <div>
                        <h3>Property Location</h3>

                        <p>Add the complete address of your accommodation.</p>
                    </div>
                </div>

                <div className='form-grid'>
                    {/* Address Line 1 */}

                    <div className='form-field full-width'>
                        <label htmlFor='line1'>
                            Address Line 1<span className='required'>*</span>
                        </label>

                        <input
                            id='line1'
                            type='text'
                            value={address.line1 || ''}
                            placeholder='House number, street or property address'
                            disabled={!canEdit}
                            onChange={(e) => updateAddress('line1', e.target.value)}
                        />
                    </div>

                    {/* Address Line 2 */}

                    <div className='form-field full-width'>
                        <label htmlFor='line2'>Address Line 2</label>

                        <input
                            id='line2'
                            type='text'
                            value={address.line2 || ''}
                            placeholder='Area, locality or additional address'
                            disabled={!canEdit}
                            onChange={(e) => updateAddress('line2', e.target.value)}
                        />
                    </div>

                    {/* Landmark */}

                    <div className='form-field'>
                        <label htmlFor='landmark'>Landmark</label>

                        <input
                            id='landmark'
                            type='text'
                            value={address.landmark || ''}
                            placeholder='Near a known landmark'
                            disabled={!canEdit}
                            onChange={(e) => updateAddress('landmark', e.target.value)}
                        />
                    </div>

                    {/* Pincode */}

                    <div className='form-field'>
                        <label htmlFor='pincode'>Pincode</label>

                        <input
                            id='pincode'
                            type='text'
                            inputMode='numeric'
                            maxLength={10}
                            value={address.pincode || ''}
                            placeholder='e.g. 571201'
                            disabled={!canEdit}
                            onChange={(e) => updateAddress('pincode', e.target.value)}
                        />
                    </div>

                    {/* City */}

                    <div className='form-field'>
                        <label htmlFor='city'>
                            City
                            <span className='required'>*</span>
                        </label>

                        <input
                            id='city'
                            type='text'
                            value={address.city || ''}
                            placeholder='e.g. Madikeri'
                            disabled={!canEdit}
                            onChange={(e) => updateAddress('city', e.target.value)}
                        />

                        {errors.city && <span className='field-error'>{errors.city}</span>}
                    </div>

                    {/* State */}

                    <div className='form-field'>
                        <label htmlFor='state'>
                            State
                            <span className='required'>*</span>
                        </label>

                        <input
                            id='state'
                            type='text'
                            value={address.state || ''}
                            placeholder='e.g. Karnataka'
                            disabled={!canEdit}
                            onChange={(e) => updateAddress('state', e.target.value)}
                        />

                        {errors.state && <span className='field-error'>{errors.state}</span>}
                    </div>

                    {/* Country */}

                    <div className='form-field'>
                        <label htmlFor='country'>
                            Country
                            <span className='required'>*</span>
                        </label>

                        <input
                            id='country'
                            type='text'
                            value={address.country || ''}
                            placeholder='India'
                            disabled={!canEdit}
                            onChange={(e) => updateAddress('country', e.target.value)}
                        />

                        {errors.country && <span className='field-error'>{errors.country}</span>}
                    </div>
                </div>
            </section>

            {/* =====================================================
                GEO LOCATION
            ===================================================== */}

            <section className='form-section'>
                <div className='section-heading'>
                    <div>
                        <h3>Map Location</h3>

                        <p>
                            Add the exact coordinates of the property. These will be used for maps, nearby searches and
                            location-based discovery.
                        </p>
                    </div>
                </div>

                {errors.geo && (
                    <div className='form-error-summary'>
                        <strong>Location required</strong>
                        <p>{errors.geo}</p>
                    </div>
                )}

                <div className='location-coordinates'>
                    {/* Latitude */}

                    <div className='form-field'>
                        <label htmlFor='latitude'>Latitude</label>

                        <input
                            id='latitude'
                            type='number'
                            step='any'
                            value={coordinates[1] ?? ''}
                            placeholder='e.g. 12.4244'
                            disabled={!canEdit}
                            onChange={(e) => updateCoordinate(1, e.target.value)}
                        />
                    </div>

                    {/* Longitude */}

                    <div className='form-field'>
                        <label htmlFor='longitude'>Longitude</label>

                        <input
                            id='longitude'
                            type='number'
                            step='any'
                            value={coordinates[0] ?? ''}
                            placeholder='e.g. 75.7382'
                            disabled={!canEdit}
                            onChange={(e) => updateCoordinate(0, e.target.value)}
                        />
                    </div>
                </div>

                {/* Coordinate Preview */}

                <div className='coordinates-preview'>
                    <div className='coordinate-icon'>📍</div>

                    <div>
                        <strong>Property Coordinates</strong>

                        <span>
                            Longitude: {coordinates[0] || 'Not set'}
                            {'  '}·{'  '}
                            Latitude: {coordinates[1] || 'Not set'}
                        </span>
                    </div>
                </div>

                {/* =====================================================
                    INTERACTIVE MAP
                    ===================================================== */}

                <LocationMap
                    latitude={typeof coordinates[1] === 'number' ? coordinates[1] : null}
                    longitude={typeof coordinates[0] === 'number' ? coordinates[0] : null}
                    disabled={!canEdit}
                    onLocationChange={(latitude, longitude) => {
                        updateAddress('geo', {
                            type: 'Point',
                            coordinates: [longitude, latitude]
                        });
                    }}
                />
            </section>

            {/* =====================================================
                LOCATION INFORMATION
            ===================================================== */}

            <section className='form-section'>
                <div className='section-heading'>
                    <div>
                        <h3>Location Information</h3>

                        <p>This information helps travelers understand where the property is located.</p>
                    </div>
                </div>

                <div className='location-info-grid'>
                    <div className='location-info-card'>
                        <span className='location-info-label'>City</span>

                        <strong>{address.city || 'Not added'}</strong>
                    </div>

                    <div className='location-info-card'>
                        <span className='location-info-label'>State</span>

                        <strong>{address.state || 'Not added'}</strong>
                    </div>

                    <div className='location-info-card'>
                        <span className='location-info-label'>Country</span>

                        <strong>{address.country || 'Not added'}</strong>
                    </div>

                    <div className='location-info-card'>
                        <span className='location-info-label'>Pincode</span>

                        <strong>{address.pincode || 'Not added'}</strong>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default LocationSection;
